import { useSlackSenderApi } from "../../../../services/api/SlackSenderApi";
import { SelectorIcon } from "../../../employees/fields/EmployeeFieldSelector";
import { Icon, Select, Stack, Title } from "@introist/react-foundation/v2";
import { ListItem } from "../../../../foundation/blocks/ListItem";
import { TagSelector } from "../../../system/tags/TagSelector";
import { isTagPath, TagPathTitle } from "../../../system/tags/TagPathTitle";
import { isEmail } from "../../../../utils/string-templater";

type SlackUserSelectProps = {
  senderId?: string;
  value: string[];
  onChange: (newValue: string[]) => unknown;
  error?: boolean;
  workflowId: string;
};

export const SlackUserSelect = ({
  senderId,
  value,
  onChange,
  workflowId
}: SlackUserSelectProps) => {
  const slackSenderApi = useSlackSenderApi();
  const { data: slackUsers } = slackSenderApi.listUsers(
    { senderId: senderId || "" },
    { enabled: !!senderId }
  ).query;

  const slackUserOptions = (slackUsers ?? []).map(user => ({
    key: user.id,
    title: user.realName,
    startAdornment: <SelectorIcon icon="person" />
  }));

  const resolveTitle = (value: string) => {
    if (isEmail(value)) return value;

    const user = slackUsers?.find(u => u.id === value);
    if (user) return user.realName;

    if (isTagPath(value)) {
      return <TagPathTitle path={value} workflowId={workflowId} />;
    }

    return value;
  };

  return (
    <Stack vertical gap="xSmall">
      {(value ?? []).map((path: string) => (
        <ListItem onRemove={() => onChange(value.filter(v => v !== path))}>
          <Stack style={{ width: "100%" }} gap="small">
            <SelectorIcon icon={isTagPath(path) ? "attribute" : "person"} />
            <Title variant="bold">{resolveTitle(path)}</Title>
          </Stack>
        </ListItem>
      ))}
      <Stack
        gap="small"
        style={{ marginTop: (value ?? []).length > 0 ? "var(--spacing-small)" : undefined }}
      >
        <TagSelector
          variant="blended"
          size="small"
          title="Add field"
          onSelect={tag => onChange([...value, tag.tag])}
          ctx={{
            workflowId,
            fieldTypes: ["email", "employee"]
          }}
        />
        {slackUserOptions.length > 0 && (
          <Select
            element="button"
            variant="blended"
            size="small"
            attachToRef={false}
            searchable
            placeholder="Add Slack user"
            startAdornment={<Icon name="plusSmall" />}
            options={slackUserOptions}
            onSelect={opt => onChange([...value, opt.key])}
          />
        )}
      </Stack>
    </Stack>
  );
};
